/**
 * Runtime registry — loads packs and exposes lookup of element types,
 * relation types, view kinds, and the renderings that bind concepts to
 * specific notation visualizations.
 *
 * Workspace-specific subtypes (Specialization) added via RegistryExtensions
 * are merged on top, with `extends_type_id` pointing to the parent.
 */
import type { BilingualText } from "./types";
import {
  type AttributeValidationError,
  type ValidateOptions,
  validateAttributes,
} from "./validation";

/**
 * M6.5-W3b — folder-classes voor type-gegroepeerde Base-folders.
 * Drop-validatie matcht `folder.type_group` ↔ resolved class van de
 * drag-source.
 */
export type FolderClass = "archimate" | "sharp" | "bmc" | "c4";

/**
 * Resolve folder-class voor een bestaand concept (move-source of
 * server-side create-validatie). Inspectie volgt typeId-prefix + alle
 * stereotype-id-prefixen; eerste hit wint, anders fallback archimate.
 */
export function resolveFolderClassForConcept(
  typeId: string,
  stereotypes: readonly string[] | undefined,
): FolderClass {
  const candidates = [typeId, ...(stereotypes ?? [])];
  for (const id of candidates) {
    if (id.startsWith("c4.")) return "c4";
    if (id.startsWith("sharp.")) return "sharp";
    if (id.startsWith("bmc.")) return "bmc";
  }
  return "archimate";
}

export interface AttributeSchema {
  name: string;
  label: BilingualText;
  description?: BilingualText;
  type: string;
  options?: string[];
  /**
   * Display labels per enum option, keyed by the option id. Lets the UI
   * show human-readable / bilingual names instead of the raw enum value
   * (e.g. `read-write` → "lezen en schrijven").
   */
  option_labels?: Record<string, BilingualText>;
  owner_only?: boolean;
  /**
   * Virtuele opslag-routering (M5+). Het attribuut leest/schrijft naar
   * een top-level concept-slot i.p.v. naar `concept.attributes[name]`.
   *
   * Modi:
   *  - `"concept_description"` — routeert naar `concept.description`.
   *    Gebruik voor stereotype-attributen die conceptueel synoniem zijn
   *    met de ArchiMate-description-slot (Sharp `«Concept».definition_text`,
   *    C4 `«Software System/Container/Component».description_text`,
   *    Risk `«Risk».risk_description`, etc.).
   *  - `"concept_name"` — routeert naar `concept.name`. Gebruik voor
   *    "label"-attributen op stereotypes die het conceptueel dezelfde
   *    rol als de ArchiMate-name vervullen (Risk `«Risk».risk_label`,
   *    `«Vulnerability».vulnerability_label`, etc.). Zo voorkom je
   *    dubbele invoer + drift tussen concept-naam en stereotype-label.
   *
   * Effecten:
   * - Storage: één canonical waarde per concept; geen drift tussen
   *   ArchiMate-base-veld en stereotype-veld.
   * - UI: AttributePanel verbergt de kernel-Name/Beschrijving zodra een
   *   actief stereotype dit veld declareert; het virtuele veld
   *   verschijnt als eerste in z'n stereotype-bundle.
   * - Validatie: required-checks lezen uit `concept.name` /
   *   `concept.description`.
   * - Multi-stereotype: meerdere stereotypes met dit veld op één
   *   concept delen dezelfde slot — semantisch correct.
   */
  storage_mode?: "concept_description" | "concept_name";
  /**
   * Open-enum-modus (alleen voor `enum`/`enum?`-types). Wanneer
   * `true`: de UI rendert een combobox (input + datalist) i.p.v.
   * een gesloten select; modelleur kan een waarde uit `options`
   * kiezen of vrij intypen. Validator accepteert dan elke string —
   * de `options`-lijst dient als suggestie/auto-complete, niet als
   * sluitend domein. Gebruik wanneer de waardenset in de praktijk
   * nooit volledig kan zijn (bv. cloud-providers, libraries).
   */
  freeform?: boolean;
  /**
   * Voor `reference`/`reference?`/`reference[]`-types: scope-filter voor
   * de element-picker. Niet gezet of leeg = geen filter (de picker toont
   * alle workspace-concepten). Wanneer wél gezet matcht een concept
   * wanneer z'n base-type in `type_ids` zit OF z'n stereotypes overlapt
   * met `stereotype_ids` (union-OR).
   */
  reference_target?: {
    type_ids?: string[];
    stereotype_ids?: string[];
  };
}

export interface ElementType {
  id: string;
  label: BilingualText;
  description?: BilingualText;
  layer: string;
  aspect: string;
  fase1_active: boolean;
  icon_id?: string;
  attributes?: AttributeSchema[];
  valid_parent_types?: string[];
}

export interface RelationType {
  id: string;
  label: BilingualText;
  description?: BilingualText;
  category: string;
  fase1_active: boolean;
  arrow_style?: string;
  directionality?: "directed" | "undirected";
  attributes?: AttributeSchema[];
}

export interface ViewKind {
  id: string;
  name: BilingualText;
  description?: BilingualText;
  fase1_active: boolean;
  visible_layers?: string[];
  visible_aspects?: string[];
  included_types?: string[];
  excluded_types?: string[];
  /**
   * When set, only the listed relation type ids may be drawn between
   * concepts on a diagram of this view kind. The matrix still validates
   * which relations are *valid*; this list narrows that down further.
   * Absent / empty array means "no extra restriction beyond the matrix".
   */
  included_relation_types?: string[];
  /**
   * Optional relation-type id picked by default when the user connects
   * two nodes on a diagram of this view kind, mits de matrix die
   * toelaat. Bv. `archimate.Association` op `sharp.contextual` zodat
   * Sharp Concepts standaard met een neutrale Association worden
   * verbonden i.p.v. Serving (de globale fallback). Wanneer afwezig
   * valt Canvas terug op de globale RELATION_TYPE_PREFERENCE-lijst.
   */
  default_relation_type?: string;
  /**
   * Workspace-defined custom viewpoints set this so the UI can
   * distinguish them from the static Open Group ones bundled in the
   * pack. Open Group viewpoints leave it false / undefined.
   */
  custom?: boolean;
  /**
   * **Label-resolver** voor deze view-kind (M5-§3). Wanneer gezet:
   * de canvas + onderwater-venster gebruiken `concept.altLabels[<key>]`
   * (en `relation.altLabels[<key>]`) als label, met fallback op de
   * standaard `name` wanneer dat alt-label ontbreekt of leeg is.
   * Voorbeeld: een Sharp Concept Model View zet
   * `label_resolver: "sharp"` zodat `sharpLabel: "Klant"` wordt
   * getoond in plaats van de ArchiMate-naam (mogelijk technische
   * representatie). Een C4 Container View zet
   * `label_resolver: "c4"`. Wanneer leeg of undefined → standaard
   * `name`-pad.
   */
  label_resolver?: string;
  /**
   * BMC Pakket B (M5.5/M6.0): rendering-modus voor deze view-kind.
   * - `"free"` (default wanneer afwezig) — standaard React Flow met
   *   x/y-coördinaten op nodes (huidige gedrag, geldt voor alle
   *   bestaande view-kinds).
   * - `"canvas"` — grid-layout via `canvas_template_id`. Nodes hebben
   *   `slot_id` (in viewState) i.p.v. position. Renderer-implementatie
   *   volgt; tot dan rendert canvas-mode als free-form (graceful
   *   degradation — geen breakage tijdens incrementele rollout).
   *
   * Zie `archimate-bmc-extensie.md` §7 voor het generieke canvas-
   * mechanisme dat als basis dient voor BMC + Lean Canvas + SWOT +
   * Empathy Map + Value Proposition Canvas.
   */
  layout_mode?:
    | "free"
    | "canvas"
    | "heat-map"
    | "roadmap"
    | "plateau-diff"
    | "gap-diff"
    | "glossary"
    | "roadmap-matrix"
    | "swimlane";
  /**
   * BMC Pakket B: verplicht wanneer `layout_mode = "canvas"`. Verwijst
   * naar een `CanvasTemplate.id` geregistreerd via een pack's
   * `canvas_templates`-sectie.
   */
  canvas_template_id?: string;
  /**
   * Wanneer een concept met een base-type uit `applies_to` op een
   * diagram van deze view-kind wordt geplaatst (via palette-drop of
   * Explorer-drag), wordt automatisch `stereotype_id` toegevoegd aan
   * z'n `stereotypes`-array — mits het stereotype er nog niet op zit.
   * Bedoeld voor view-kinds die een specifieke modeleer-context
   * opleggen, bv. de Risk-and-Security-view die elk geplaatst
   * asset-eligible concept als «risk.Asset» markeert.
   */
  auto_apply_stereotype?: {
    stereotype_id: string;
    applies_to: string[];
  };
  /**
   * Toon per stereotype z'n `status_indicator`-bolletje linksboven op
   * elk concept op deze view-kind. Default `false` / afwezig — alleen
   * dedicated overview-view-kinds zoals `risk.security-overview` zetten
   * dit aan zodat de status-bolletjes andere views niet vervuilen.
   */
  show_stereotype_status_indicators?: boolean;
  /**
   * Diagram-niveau attribuut-schema's voor view-kinds met eigen
   * configuratie. Gerendered door AttributePanel zodra een diagram van
   * deze view-kind actief is en geen concept/relation geselecteerd.
   * Waarden landen op `Diagram.attributes` (parallel aan
   * `Concept.attributes`).
   *
   * Eerste use-case: `analytics.heat-map` declareert hier target_type,
   * vertical_attribute, horizontal_attribute en de vier kleur-velden.
   * Sommige van die velden hebben dynamische opties (afhankelijk van
   * andere keuzes binnen dezelfde config) — AttributePanel kent een
   * special-case voor heat-map om die runtime-options te leveren.
   */
  attributes?: AttributeSchema[];
}

/**
 * BMC Pakket B (M5.5/M6.0) — generiek canvas-mechanisme. Een
 * `CanvasTemplate` is een herbruikbare layout-definitie met grid +
 * benoemde slots + per-slot toegestane stereotypes. Eerste invulling:
 * `bmc.canvas.v1` (Osterwalder 5×3-grid) — wordt in Pakket B aan
 * `bmc-pack.json` toegevoegd zodra de grid-renderer in `apps/web` staat.
 *
 * Toekomstige templates op hetzelfde mechanisme: Lean Canvas (Ash
 * Maurya), SWOT, Value Proposition Canvas (Strategyzer), Empathy Map.
 * Templates leven per pack onder `pack.canvas_templates`.
 *
 * Zie `archimate-bmc-extensie.md` §7 voor de volledige spec.
 */
export interface CanvasTemplate {
  /** Uniek binnen tool. Conventie: `pack.template.version`. */
  id: string;
  name: BilingualText;
  description?: BilingualText;
  /** Semver. Templates zijn immutable na publicatie; nieuwe versie = nieuw id (.v2). */
  version: string;
  grid: GridDefinition;
  slots: CanvasSlot[];
}

export interface GridDefinition {
  rows: number;
  columns: number;
  /** Optioneel CSS-grid-stijl, bv. `["1fr", "1fr", "0.6fr"]`. Lengte = rows wanneer gezet. */
  row_heights?: string[];
  /** Optioneel CSS-grid-stijl, bv. `["1fr", "1fr", "1.2fr", "1fr", "1fr"]`. */
  column_widths?: string[];
}

export interface CanvasSlot {
  /** Uniek binnen template, kebab-case. */
  id: string;
  name: BilingualText;
  description?: BilingualText;
  grid_row: number;
  grid_column: number;
  /** Default 1. */
  row_span?: number;
  /** Default 1. */
  col_span?: number;
  /** Welke pack-stereotypes mogen in dit slot. Drop-validator weigert non-matching. */
  allowed_stereotypes: string[];
  /** Default 0. Voor completeness-validatie ("Canvas Health"-rapport). */
  min_items?: number;
  /** Default `"*"` (onbeperkt). */
  max_items?: number | "*";
  /** Optionele hex-tint voor cell-achtergrond, bv. `"#FFF7E0"`. */
  slot_color?: string;
}

export interface Pack {
  pack_id: string;
  pack_name: BilingualText;
  pack_version: string;
  depends_on?: string[];
  types?: ElementType[];
  relations?: RelationType[];
  views?: ViewKind[];
  /**
   * Pack-shipped subtypes that extend a type from a depended-on pack.
   * Same shape and semantics as a workspace-defined `RegistryExtension`,
   * but registered automatically when the pack loads. Used by the C4
   * pack to provide e.g. `c4.Person` as a subtype of
   * `archimate.BusinessActor` — own label and own C4-specific attributes,
   * but inherits layer/aspect/icon from the parent so the matrix and
   * canvas treat it like its parent for compatibility purposes.
   */
  extensions?: RegistryExtension[];
  /**
   * Pack-shipped stereotypes (M5+). Zie `StereotypeBundle` voor
   * details. Eén pack kan stereotypes definiëren die andere packs
   * targeten via `applies_to` — bv. de C4-pack levert
   * `«Software System»` op `archimate.ApplicationComponent` zonder
   * de ArchiMate-pack te muteren.
   */
  stereotypes?: StereotypeBundle[];
  /**
   * BMC Pakket B (M5.5/M6.0): canvas-template-definities die door
   * view_kinds met `layout_mode = "canvas"` via `canvas_template_id`
   * worden geconsumeerd. Templates leven per pack zodat BMC en Lean
   * Canvas / SWOT / etc. naast elkaar kunnen bestaan zonder
   * mechanisme-aanpassingen. Pakket A laat dit veld leeg; eerste
   * invulling (`bmc.canvas.v1`, Osterwalder 5×3) komt in Pakket B
   * samen met de grid-renderer.
   */
  canvas_templates?: CanvasTemplate[];
  /**
   * Optional virtual palette groupings. The C4 pack uses this to
   * present a "C4 model" group containing entries that map onto
   * existing ArchiMate types with preset attribute defaults — e.g.
   * a "Software System (C4)" entry that creates an
   * `archimate.ApplicationComponent` with `c4_level=system`. This
   * avoids duplicating types in the registry while still giving the
   * user a curated entry-point per modelling style.
   */
  palette_groups?: PaletteGroup[];
}

export interface PaletteGroup {
  id: string;
  label: BilingualText;
  description?: BilingualText;
  /** Entries shown in the order declared. */
  entries: PaletteEntry[];
  /**
   * Render-positie van de groep in het type-palette. Wanneer gezet
   * verschijnt deze groep direct na de gegeven ArchiMate-laag-groep
   * in de palette-volgorde (bv. `"strategy"` → tussen Strategy en
   * Business). Niet-gezet of onbekende layer-id = aan het einde, na
   * alle layer-groepen.
   */
  after_layer?: string;
  /**
   * Whitelist van view-kind-ids waarop deze palette-group zichtbaar
   * mag zijn. Wanneer ontbrekend of leeg = geen view-restrictie
   * (groep verschijnt op elk diagram, mits de per-entry
   * type-allowance pass't). Wanneer gezet = groep verbergen op alle
   * andere view-kinds, ook al zou een individuele entry's base-type
   * toelaten.
   *
   * Use-case: voorkomt dat de "C4 model"-groep op een
   * `sharp.contextual` diagram alsnog Persoon (BusinessActor-base)
   * + Boundary (Grouping-base) toont enkel omdat hun base-types
   * toevallig in de Sharp-included_types staan. C4 hoort daar
   * spec-technisch niet — pack-author bevestigt dat hier expliciet.
   */
  applies_to_view_kinds?: string[];
  /**
   * M6.5-W3b — folder-class waar drops uit deze palette-groep onder
   * vallen. Bepaalt of een drop op een Base-folder geaccepteerd wordt:
   * `folder.type_group === paletteGroup.folder_class` is een match.
   * Bij absentie geldt de default `"archimate"` voor alle ArchiMate-
   * laag-groepen die auto-gegenereerd worden uit ElementType.layer.
   * Drawing-pack laat dit veld bewust leeg — drawing-primitieven
   * mogen niet in folders worden gedropped.
   */
  folder_class?: "archimate" | "sharp" | "bmc" | "c4";
}

export interface PaletteEntry {
  /** Element-type id (or extension id) that the entry creates. */
  type_id: string;
  /**
   * Wat voor type entry dit is. Default `"element"` (drag → concept-
   * create). `"relation"` betekent dat de entry verwijst naar een
   * relatie-type — niet sleepbaar op leeg canvas; wordt aangeboden via
   * het relatie-keuzemenu wanneer de gebruiker tussen twee concepten
   * een verbinding maakt.
   */
  kind?: "element" | "relation";
  /** Override label shown in the palette. Falls back to the type's label. */
  label_override?: BilingualText;
  description?: BilingualText;
  /**
   * Attribute defaults applied at concept-creation time when the user
   * drops this entry onto the canvas. Lets a single ArchiMate type
   * surface multiple semantically-distinct palette entries (e.g.
   * ApplicationComponent → System / Container / Component variants
   * via the `c4_level` attribute).
   */
  default_attributes?: Record<string, unknown>;
  /**
   * Stereotype-ids automatisch toegepast bij drop (M5+). Laat een
   * palette-entry op een ArchiMate-basis-type direct een stereotype
   * meegeven, bv. "Concept" → typeId `archimate.BusinessObject` +
   * `default_stereotypes: ["sharp.Concept"]`. Het concept landt dan
   * met `stereotypes: ["sharp.Concept"]` in de repo zonder dat de
   * gebruiker de tag-UI apart hoeft te openen.
   */
  default_stereotypes?: string[];
  /**
   * Wanneer `true` wordt deze type-id NIET ook in zijn ArchiMate-
   * laag-groep getoond — alleen via deze palette-group. Default
   * `false` (de C4-conventie waarin bv. `archimate.ApplicationComponent`
   * zowel als "Software System" in de C4-groep staat ALS onder de
   * Application-laag). Sharp's `sharp.Attribute` / `sharp.Identifier`
   * zetten dit op `true` zodat ze niet ook onder Composite verschijnen.
   */
  exclusive?: boolean;
  /**
   * View-kind-filter op entry-niveau (analoog aan
   * `PaletteGroup.applies_to_view_kinds`). Gebruik wanneer een entry
   * in een al-getoonde groep alleen relevant is op specifieke views.
   * Voorbeeld: BusinessProcess als DATA-MODELING-entry alleen op
   * `sharp.data-process-connection`. Leeg/afwezig = geen restrictie.
   */
  applies_to_view_kinds?: string[];
  /**
   * Tegenpool van `applies_to_view_kinds`: deze entry wordt VERBORGEN
   * op de genoemde view-kinds, ook al staat z'n base-type in
   * `included_types`. Handig wanneer de C4-MODEL-groep generiek
   * 'overal aan' staat maar specifieke entries (bv. Component,
   * Container Instance, Enterprise Boundary) op de Container-View
   * niet horen. Wint van `applies_to_view_kinds` wanneer beide gezet.
   */
  excluded_view_kinds?: string[];
}

export interface RegistryExtension {
  id: string;
  parent_type_id: string;
  label: BilingualText;
  description?: BilingualText;
  added_attributes?: AttributeSchema[];
}

/**
 * **Stereotype** — een view-laag bovenop een ArchiMate-element die
 * extra metadata (eigen label, eigen attributen, eigen notatie)
 * meebrengt zonder het basis-element te muteren. Eén concept of
 * relatie kan 0..* stereotypes dragen.
 *
 * Verschilt fundamenteel van `RegistryExtension` (Specialization):
 * - **Specialization** = nieuw subtype binnen één type-set
 *   (PaymentService extends ApplicationService) — eigen plek in
 *   de type-hiërarchie via `extends_type_id`
 * - **Stereotype** = view-laag bovenop een bestaand element
 *   («Container» op ApplicationComponent) — element behoudt z'n
 *   identiteit, label-resolver kiest welke naam getoond wordt
 *   per viewpoint
 *
 * Polymorfie via `applies_to: string[]`: één stereotype kan op
 * meerdere base-types — zo is C4's `«Container»` polymorf op
 * `archimate.ApplicationComponent` (app-code) én
 * `archimate.SystemSoftware` (infrastructure). Sub-stereotype
 * (bv. `«Database»`) bepaalt via `extends_stereotype` welke
 * base-type van het bovenliggende stereotype gekozen wordt.
 *
 * Zie [`docs/architecture/archimate-c4-extensie.md`](../../../docs/architecture/archimate-c4-extensie.md)
 * en [`docs/architecture/archimate-sharp-extensie.md`](../../../docs/architecture/archimate-sharp-extensie.md)
 * voor de inhoudelijke C4 + Sharp Concept Modelling-extensies die
 * dit mechanisme gebruiken (M5+).
 */
/**
 * Eén entry in een stereotype's `applies_to`.
 *
 * **De kale string-vorm** (`"archimate.BusinessProcess"`) is de originele
 * en verreweg de meest voorkomende: het stereotype mag op dat base-type
 * landen, punt. De sentinel `"*"` betekent "elk base-type".
 *
 * **De object-vorm met `requires_stereotype`** bestaat sinds de
 * ArchiMate-4-migratie (fase 1). Die merge maakte de laag-specifieke
 * gedragstypes generiek — `BusinessProcess`, `ApplicationProcess` en
 * `TechnologyProcess` werden één `archimate.Process` — waardoor
 * `applies_to` z'n discriminator verloor. Een stereotype als
 * `bmc.CustomerRelationship`, dat semantisch alleen op een BEDRIJFSproces
 * slaat, zou na een naïeve migratie ook op ex-technologieprocessen
 * toepasbaar worden.
 *
 * De conditie herstelt dat door te eisen dat het concept óók een bepaald
 * (compat-)stereotype draagt:
 *
 * ```json
 * "applies_to": [
 *   { "type_id": "archimate.Process",
 *     "requires_stereotype": "archimate31.BusinessProcess" }
 * ]
 * ```
 *
 * Dat sluit aan bij hoe ArchiMate 4 het zelf bedoelt: het
 * domein-onderscheid ís een specialisatie, geen apart type.
 *
 * Zie `docs/architecture/archimate4-migratie.md` §4 (fase 1).
 */
export type AppliesToEntry =
  | string
  | {
      /** Het base-type-id waarop dit stereotype mag landen. */
      type_id: string;
      /**
       * Wanneer gezet: het stereotype mag alleen op dit type landen als
       * het concept dít stereotype óók draagt. Weggelaten = de entry
       * gedraagt zich als de kale string-vorm.
       */
      requires_stereotype?: string;
    };

export interface StereotypeBundle {
  /** Naam van een SVG uit de icoon-bibliotheek, bv. `risk-security/risk`.
   *  Bedoeld voor zelf gedefinieerde profielen: de pack-profielen staan in
   *  hardcoded maps, maar een profiel uit de database kan daar niet in staan
   *  en wijst hier z'n icoon aan. Kiezen uit de meegeleverde set, want die
   *  SVG's gebruiken `stroke="currentColor"` en kleuren dus mee met de rand. */
  icon_id?: string;
  /** Stable id zoals `c4.Container`, `sharp.Concept`. */
  id: string;
  /** Weergavelabel zoals «Container» of «Concept». */
  label: BilingualText;
  description?: BilingualText;
  /**
   * Welke base ElementType-ids dit stereotype mag dragen. Eén
   * stereotype kan polymorf zijn (Container kan op
   * ApplicationComponent OF SystemSoftware) — vandaar string[].
   * Voor relatie-stereotypes (bv. `«C4 Connection»`) wijst dit
   * naar relation-type ids.
   *
   * Wanneer `extends_stereotype` is gezet en `applies_to` ontbreekt,
   * erft de stereotype de `applies_to` van zijn parent.
   */
  /**
   * Lijst base-type-ids waarop dit stereotype mag landen. Speciale
   * waarde `"*"` betekent "elk base-type" — gebruikt door transversale
   * markers zoals `«Reference Object»` (M6.5-W3) die niet aan een
   * specifieke laag gebonden zijn.
   *
   * Een item mag ook een object zijn met een `requires_stereotype`-
   * conditie — zie {@link AppliesToEntry}.
   */
  applies_to?: AppliesToEntry[];
  /**
   * Sub-stereotype is een verfijning van een ander stereotype
   * (bv. `c4.Microservice` extends `c4.Container`). Sub-stereotypes
   * erven `applies_to` van parent tenzij ze 'm overrulen, en
   * stapelen attributen.
   */
  extends_stereotype?: string;
  /**
   * Aanvullende attributen specifiek voor dit stereotype, bovenop
   * de attributes van de base ElementType / RelationType. Worden
   * gemerged met parent-stereotype's attributen via dezelfde
   * `mergeExtension`-logica als RegistryExtension's
   * `added_attributes`.
   */
  attributes?: AttributeSchema[];
  /**
   * Naam van een aanvullend label-veld op het concept/relatie
   * (bv. `c4Label`, `sharpLabel`). De viewpoint-resolver kiest
   * tijdens render of `name` (ArchiMate) of dit veld getoond
   * wordt. Optioneel — sommige stereotypes hebben geen eigen
   * label-naamgeving.
   */
  label_field?: string;
  /**
   * Whitelist van relation-type-ids die relevant zijn wanneer een
   * endpoint dit stereotype draagt. Gebruikt door de relatie-type-
   * picker om de keuze te beperken tot wat semantisch klopt voor
   * dit stereotype. Niet gezet = geen restrictie vanuit dit
   * stereotype (picker valt terug op de prefix-based extension-
   * filter). Union-semantiek: als source- of target-endpoint
   * stereotypes hebben met deze lijst, vormen alle allow-lijsten
   * samen + ArchiMate-base-relaties (zonder extension-prefix) het
   * toegestane geheel.
   */
  allowed_relation_types?: string[];
  /**
   * Notation-info voor de canvas-renderer. Niet gebruikt in de
   * validatie-laag.
   *
   * `registry/notation-tokens.json` blijft leidend: staat daar een token
   * voor dit profiel, dan wint dat. Wat hier staat is de terugval, en dat
   * is precies wat een zelf gedefinieerd profiel nodig heeft — dat kán geen
   * token hebben, want de tokens zijn een bestand en het profiel een rij.
   * Zonder dit valt zo'n profiel terug op de laagkleur van z'n basistype en
   * is het op het canvas niet te onderscheiden.
   */
  notation?: {
    fill?: string;
    stroke?: string;
    text_color?: string;
    glyph?: string;
    label_prefix?: string;
  };
  /**
   * Status-indicator (gekleurd bolletje linksboven op het concept) op
   * view-kinds met `show_stereotype_status_indicators: true`. `attribute`
   * is de naam van een enum-attribute op het concept; `colors` mapt elke
   * enum-waarde naar een kleur-token ("green" | "yellow" | "orange" |
   * "red"). De UI rendert het bolletje wanneer de view-kind de feature
   * aan heeft staan EN de attribute-waarde een match heeft in `colors`.
   * Niet-gemapte / lege attribuut-waarden tonen geen bolletje.
   */
  status_indicator?: {
    attribute: string;
    colors: Record<string, "green" | "yellow" | "orange" | "red">;
  };
}

export class RegistryRuntime {
  private readonly packs = new Map<string, Pack>();
  private readonly elementTypes = new Map<string, ElementType>();
  private readonly relationTypes = new Map<string, RelationType>();
  private readonly viewKinds = new Map<string, ViewKind>();
  private readonly extensions = new Map<string, RegistryExtension>();
  private readonly stereotypes = new Map<string, StereotypeBundle>();
  /** De pack-definities apart bewaard, zodat dynamische profielen terug te
   *  draaien zijn zonder de pack-laag te beschadigen — zie
   *  `setDynamicProfiles`. */
  private readonly packStereotypes = new Map<string, StereotypeBundle>();
  private readonly paletteGroups: PaletteGroup[] = [];
  private readonly canvasTemplates = new Map<string, CanvasTemplate>();

  registerPack(pack: Pack): void {
    this.packs.set(pack.pack_id, pack);
    for (const t of pack.types ?? []) {
      this.elementTypes.set(t.id, t);
    }
    for (const r of pack.relations ?? []) {
      this.relationTypes.set(r.id, r);
    }
    for (const v of pack.views ?? []) {
      this.viewKinds.set(v.id, v);
    }
    // Pack-shipped extensions go into the same `extensions` map as
    // workspace-defined ones — `getElementType` returns the merged-form
    // for either source, transparent to callers.
    for (const ext of pack.extensions ?? []) {
      this.extensions.set(ext.id, ext);
    }
    // Pack-shipped stereotypes (M5+). Net als extensions: één map
    // voor alle bronnen. Future workspace-defined stereotypes (als
    // ze ooit komen) kunnen via dezelfde map.
    for (const st of pack.stereotypes ?? []) {
      this.stereotypes.set(st.id, st);
      this.packStereotypes.set(st.id, st);
    }
    for (const group of pack.palette_groups ?? []) {
      this.paletteGroups.push(group);
    }
    // BMC Pakket B: canvas-templates van alle packs centraal
    // verzamelen. Een view_kind met layout_mode = "canvas" looked-up
    // z'n template via getCanvasTemplate(canvas_template_id).
    for (const tpl of pack.canvas_templates ?? []) {
      this.canvasTemplates.set(tpl.id, tpl);
    }
  }

  /**
   * Virtual palette groups contributed by all loaded packs (in pack
   * registration order). The TypePalette renders these as additional
   * accordions alongside the layer-based groupings.
   */
  listPaletteGroups(): PaletteGroup[] {
    return [...this.paletteGroups];
  }

  /**
   * M6.5-W3b — folder-class horend bij een palette-groep (bron van een
   * drag). Default `"archimate"` wanneer de groep geen `folder_class`
   * declareert of wanneer er geen virtuele groep is (= drag uit een
   * auto-gegenereerde ArchiMate-laag-groep zoals Strategy/Business/…).
   */
  getPaletteGroupFolderClass(paletteGroupId: string | null): FolderClass {
    if (!paletteGroupId) return "archimate";
    const grp = this.paletteGroups.find((g) => g.id === paletteGroupId);
    return grp?.folder_class ?? "archimate";
  }

  /**
   * BMC Pakket B (M5.5/M6.0): lookup voor canvas-templates die door
   * view_kinds met `layout_mode = "canvas"` worden geconsumeerd.
   * Returns undefined wanneer 't template-id niet geregistreerd is.
   */
  getCanvasTemplate(id: string): CanvasTemplate | undefined {
    return this.canvasTemplates.get(id);
  }

  /** Alle geregistreerde canvas-templates over alle packs heen. */
  listCanvasTemplates(): CanvasTemplate[] {
    return [...this.canvasTemplates.values()];
  }

  registerExtension(ext: RegistryExtension): void {
    this.extensions.set(ext.id, ext);
  }

  removeExtension(id: string): void {
    this.extensions.delete(id);
  }

  /**
   * Register a single view-kind at runtime, separate from a Pack. Used
   * for workspace-scoped custom viewpoints loaded from D1: the static
   * Open Group catalog comes from the pack, the per-workspace ones are
   * appended after hydrate.
   */
  registerViewKind(view: ViewKind): void {
    this.viewKinds.set(view.id, view);
  }

  unregisterViewKind(id: string): void {
    this.viewKinds.delete(id);
  }

  /** Replace every custom view-kind with a fresh set (used by re-hydrate). */
  replaceCustomViewKinds(views: ViewKind[]): void {
    for (const [id, v] of this.viewKinds) {
      if (v.custom) this.viewKinds.delete(id);
    }
    for (const v of views) {
      this.viewKinds.set(v.id, { ...v, custom: true });
    }
  }

  // ---- Lookups -----------------------------------------------------------

  getElementType(typeId: string): ElementType | undefined {
    const ext = this.extensions.get(typeId);
    if (ext) {
      const parent = this.elementTypes.get(ext.parent_type_id);
      if (!parent) {
        return undefined;
      }
      return mergeExtension(parent, ext);
    }
    return this.elementTypes.get(typeId);
  }

  getRelationType(typeId: string): RelationType | undefined {
    return this.relationTypes.get(typeId);
  }

  getViewKind(viewId: string): ViewKind | undefined {
    return this.viewKinds.get(viewId);
  }

  /**
   * All active element types — both bare pack-shipped types AND
   * extension-derived ones (the pack-shipped subtypes plus any
   * workspace-defined custom subtypes). Extensions are surfaced via
   * `mergeExtension` so callers see a fully-formed `ElementType` with
   * the parent's layer/aspect inherited, even though only the
   * extension's id+label+description+attributes were declared.
   *
   * Activeness check: extensions inherit `fase1_active` from their
   * parent; an extension whose parent is fase1_active=false is also
   * hidden, and extensions with a missing parent are skipped.
   */
  listActiveElementTypes(): ElementType[] {
    const out: ElementType[] = [...this.elementTypes.values()].filter((t) => t.fase1_active);
    for (const ext of this.extensions.values()) {
      const parent = this.elementTypes.get(ext.parent_type_id);
      if (!parent?.fase1_active) continue;
      out.push(mergeExtension(parent, ext));
    }
    return out;
  }

  listActiveRelationTypes(): RelationType[] {
    return [...this.relationTypes.values()].filter((r) => r.fase1_active);
  }

  /**
   * IDs van alle geregistreerde extensions (specializations). Gebruikt
   * door de TypePalette om extensions die ook in een `palette_groups`-
   * entry verschijnen uit te filteren uit hun parent's laag-groep —
   * anders zie je een extension als `c4.Person` zowel in BUSINESS
   * (via parent BusinessActor) als in de C4-palette-groep, wat dubbel
   * voelt.
   */
  listExtensionIds(): string[] {
    return [...this.extensions.keys()];
  }

  // === Stereotypes (M5+) ===

  /**
   * Pak een stereotype uit de map op zijn id. Returnt undefined
   * wanneer 't stereotype niet geregistreerd is.
   */
  /**
   * Voeg profielen uit de database toe aan dezelfde map als de pack-profielen.
   *
   * Twee soorten komen hierlangs. Een pack-profiel uit D1 is byte-identiek aan
   * z'n JSON-tegenhanger — de sync schrijft 'm daaruit weg en een guard bewaakt
   * de round-trip — dus dat overschrijven is aantoonbaar een no-op. Een zelf
   * gedefinieerd profiel is nieuw en komt erbij.
   *
   * **De packs blijven de terugval.** Levert de database niets, dan draait de
   * registry op wat de JSON al bood. Een lege of haperende database geeft dus
   * een werkende app, geen lege registry — dat is wat deze omzetting veilig
   * maakt om stapsgewijs te doen.
   *
   * Een profiel kan geen pack-profiel overschaduwen: dat wordt bij het
   * aanmaken geweigerd, want identiteit is de naam en één naam hoort één
   * betekenis te hebben.
   */
  registerProfiles(profiles: readonly StereotypeBundle[]): void {
    for (const profile of profiles) {
      this.stereotypes.set(profile.id, profile);
    }
  }

  /**
   * Vervang de dynamische profielen door deze set.
   *
   * Bedoeld voor de browser, waar één registry leeft die bij een
   * branch-wissel van workpackage verandert. Alleen toevoegen zou de
   * profielen van het vórige workpackage laten staan — die zijn daar niet
   * geldig, en een modelleur zou ze in de keuzelijst zien.
   *
   * Wegnemen kan niet met een simpele delete: een profielnaam kan ook uit een
   * pack komen, en dan zou verwijderen de DOM-definitie slopen. Vandaar dat de
   * pack-laag apart bewaard blijft en hier eerst wordt teruggezet.
   */
  setDynamicProfiles(profiles: readonly StereotypeBundle[]): void {
    this.stereotypes.clear();
    for (const [id, st] of this.packStereotypes) this.stereotypes.set(id, st);
    for (const profile of profiles) this.stereotypes.set(profile.id, profile);
  }

  getStereotype(id: string): StereotypeBundle | undefined {
    return this.stereotypes.get(id);
  }

  /**
   * Alle geregistreerde stereotypes — gebruik door tooling die
   * een lijst nodig heeft (palette-rendering, validatie-rapport).
   */
  listStereotypes(): StereotypeBundle[] {
    return [...this.stereotypes.values()];
  }

  /**
   * Welke relation-type-ids zijn whitelisted door minstens één van
   * de meegegeven stereotypes via hun `allowed_relation_types`-veld?
   *
   * Returnt `null` wanneer **geen enkel** stereotype een whitelist
   * declareert — caller moet dan terugvallen op de prefix-based
   * extension-filter. Returnt een (mogelijk lege) `Set<string>`
   * wanneer minstens één stereotype expliciet is; dan vormen de
   * union van alle allow-lijsten plus ArchiMate-base-relaties het
   * toegestane geheel.
   *
   * Sub-stereotypes erven `allowed_relation_types` via
   * `extends_stereotype` — een sub-stereotype dat 'm niet zelf
   * declareert, krijgt de lijst van de eerste parent in de chain die
   * 'm wel heeft. Een sub-stereotype dat 'm wél zelf declareert
   * overrulet de parent (volledige vervang, geen union). Cycles
   * worden gedetecteerd en breken de chain.
   */
  getAllowedRelationTypesForStereotypes(stereotypeIds: readonly string[]): Set<string> | null {
    let anyDeclared = false;
    const allowed = new Set<string>();
    for (const id of stereotypeIds) {
      const list = this.resolveAllowedRelationTypes(id);
      if (list === null) continue;
      anyDeclared = true;
      for (const t of list) allowed.add(t);
    }
    return anyDeclared ? allowed : null;
  }

  /**
   * Effectieve `allowed_relation_types` voor één stereotype, met
   * `extends_stereotype`-overerving. Returnt null wanneer noch het
   * stereotype zelf, noch enige parent in de chain de lijst declareert.
   */
  private resolveAllowedRelationTypes(stereotypeId: string): readonly string[] | null {
    const seen = new Set<string>();
    let cursor = this.stereotypes.get(stereotypeId);
    while (cursor) {
      if (seen.has(cursor.id)) return null; // cycle
      seen.add(cursor.id);
      if (cursor.allowed_relation_types !== undefined) return cursor.allowed_relation_types;
      if (!cursor.extends_stereotype) return null;
      cursor = this.stereotypes.get(cursor.extends_stereotype);
    }
    return null;
  }

  /**
   * Welke stereotypes mogen op een gegeven base type (element of
   * relatie). Houdt rekening met `extends_stereotype`-overerving:
   * een sub-stereotype erft de `applies_to` van zijn parent
   * tenzij het 'm overrulet.
   */
  /**
   * De effectieve type-ids waartegen stereotype-`applies_to` gematcht mag
   * worden: de type-id zelf plus — als 't een workspace-subtype
   * (RegistryExtension) is — z'n parent-keten. Zo erft een subtype
   * (bv. "AC" van ApplicationComponent) alle stereotypes die de parent
   * mag dragen. Cycle-safe.
   */
  effectiveTypeIdsFor(typeId: string): Set<string> {
    const out = new Set<string>([typeId]);
    const seen = new Set<string>();
    let ext = this.extensions.get(typeId);
    while (ext && !seen.has(ext.id)) {
      seen.add(ext.id);
      out.add(ext.parent_type_id);
      ext = this.extensions.get(ext.parent_type_id);
    }
    return out;
  }

  /**
   * Het onderliggende pack-type-id van een type-id: voor een workspace-subtype
   * (RegistryExtension) z'n verste parent in de keten (pack-type), anders de
   * type-id zelf. Gebruikt om matrix-`explicit_pairs`/`same_type`-regels op het
   * parent-type te matchen — subtypes hebben geen eigen matrix-regels. Cycle-safe.
   */
  baseTypeIdFor(typeId: string): string {
    const seen = new Set<string>();
    let current = typeId;
    let ext = this.extensions.get(current);
    while (ext && !seen.has(ext.id)) {
      seen.add(ext.id);
      current = ext.parent_type_id;
      ext = this.extensions.get(current);
    }
    return current;
  }

  /**
   * Alle stereotypes die op `baseTypeId` mogen landen.
   *
   * `presentStereotypes` = de stereotypes die het concept NU al draagt.
   * Alleen nodig wanneer een pack conditionele `applies_to`-entries
   * gebruikt (zie {@link AppliesToEntry}); laat je 'm weg, dan vallen
   * conditionele entries af. Callers die een picker vullen voor een
   * bestaand concept horen 'm dus mee te geven.
   */
  stereotypesFor(
    baseTypeId: string,
    presentStereotypes: readonly string[] = [],
  ): StereotypeBundle[] {
    const typeIds = this.effectiveTypeIdsFor(baseTypeId);
    const present = new Set(presentStereotypes);
    const out: StereotypeBundle[] = [];
    for (const st of this.stereotypes.values()) {
      if (this.matchesAppliesTo(this.resolveAppliesTo(st), typeIds, present)) {
        out.push(st);
      }
    }
    return out;
  }

  /**
   * Effectieve `applies_to`-set voor een stereotype, met
   * parent-resolutie wanneer `extends_stereotype` is gezet en
   * `applies_to` zelf ontbreekt. Detecteert cycles om infinite
   * loops te voorkomen — bij cycle returnt 'ie de eigen waarde
   * (mogelijk lege array) en gaat niet de chain in.
   *
   * Let op: de entries kunnen zowel kale strings als conditie-objecten
   * zijn. Wil je alleen de type-ids (bv. om een lijst te tonen), gebruik
   * dan {@link appliesToTypeIds}; wil je weten óf een stereotype past,
   * gebruik {@link canApplyStereotype}.
   */
  resolveAppliesTo(stereotype: StereotypeBundle): AppliesToEntry[] {
    if (stereotype.applies_to !== undefined) return [...stereotype.applies_to];
    if (!stereotype.extends_stereotype) return [];
    // Cycle-detection met seen-set
    const seen = new Set<string>([stereotype.id]);
    let current = this.stereotypes.get(stereotype.extends_stereotype);
    while (current) {
      if (seen.has(current.id)) return []; // cycle
      seen.add(current.id);
      if (current.applies_to !== undefined) return [...current.applies_to];
      if (!current.extends_stereotype) return [];
      current = this.stereotypes.get(current.extends_stereotype);
    }
    return [];
  }

  /**
   * De kale type-ids uit een stereotype's `applies_to`, met de eventuele
   * `requires_stereotype`-condities eraf gestript. Voor callers die
   * alleen willen weten "op welke types slaat dit stereotype ongeveer",
   * bijvoorbeeld om een type-filter of een lijstje te tonen.
   *
   * Gebruik dit NIET om te beslissen of een stereotype ergens op mag —
   * daarvoor is {@link canApplyStereotype}, die de condities wél
   * meeweegt.
   */
  appliesToTypeIds(stereotype: StereotypeBundle): string[] {
    return this.resolveAppliesTo(stereotype).map((e) => (typeof e === "string" ? e : e.type_id));
  }

  /**
   * Het enige punt waar een `applies_to`-set tegen een concept gematcht
   * wordt. Zowel `stereotypesFor` als `canApplyStereotype` gaan hier
   * doorheen, zodat de twee nooit uit elkaar kunnen lopen.
   */
  private matchesAppliesTo(
    entries: readonly AppliesToEntry[],
    effectiveTypeIds: ReadonlySet<string>,
    present: ReadonlySet<string>,
  ): boolean {
    for (const entry of entries) {
      // Sentinel eerst: "*" wint altijd, ongeacht type of stereotypes.
      if (entry === "*") return true;
      if (typeof entry === "string") {
        // Kale string-vorm — identiek gedrag aan vóór de conditie-uitbreiding.
        if (effectiveTypeIds.has(entry)) return true;
        continue;
      }
      if (!effectiveTypeIds.has(entry.type_id)) continue;
      if (!entry.requires_stereotype) return true;
      if (present.has(entry.requires_stereotype)) return true;
    }
    return false;
  }

  /**
   * Effectieve attribuut-set voor een stereotype incl. parent-
   * stereotype-attributen via `extends_stereotype`-keten. Eigen
   * attributen overschrijven parent-attributen met dezelfde name
   * (last-wins, zoals matrix-merge in `MatrixValidator`). Cycle-
   * safe via seen-set.
   */
  resolveStereotypeAttributes(stereotype: StereotypeBundle): AttributeSchema[] {
    const chain: StereotypeBundle[] = [stereotype];
    const seen = new Set<string>([stereotype.id]);
    let current = stereotype;
    while (current.extends_stereotype) {
      const parent = this.stereotypes.get(current.extends_stereotype);
      if (!parent || seen.has(parent.id)) break;
      seen.add(parent.id);
      chain.push(parent);
      current = parent;
    }
    // Walk parent → child zodat child overrides wint
    const merged = new Map<string, AttributeSchema>();
    for (let i = chain.length - 1; i >= 0; i--) {
      const bundle = chain[i];
      if (!bundle?.attributes) continue;
      for (const attr of bundle.attributes) {
        merged.set(attr.name, attr);
      }
    }
    return [...merged.values()];
  }

  /**
   * Validatie of een stereotype op een element/relatie van een
   * gegeven base type mag. Returnt true als baseTypeId in de
   * effectieve `applies_to`-set zit. Gebruikt door de worker-
   * PATCH-handler (M5-uitbreiding) om stereotype-misuse vroeg
   * te blokkeren.
   *
   * `presentStereotypes` = de andere stereotypes die het concept draagt.
   * **Weglaten is fail-closed**: conditionele `applies_to`-entries (zie
   * {@link AppliesToEntry}) matchen dan nooit. Valideer je een concept
   * dat meerdere stereotypes krijgt, geef dan de volledige lijst mee —
   * anders wordt een geldige combinatie ten onrechte geweigerd.
   */
  canApplyStereotype(
    stereotypeId: string,
    baseTypeId: string,
    presentStereotypes: readonly string[] = [],
  ): boolean {
    const st = this.stereotypes.get(stereotypeId);
    if (!st) return false;
    // Subtype erft de toepasbare stereotypes van z'n parent-keten.
    const typeIds = this.effectiveTypeIdsFor(baseTypeId);
    return this.matchesAppliesTo(this.resolveAppliesTo(st), typeIds, new Set(presentStereotypes));
  }

  /**
   * Valideer attribute-waarden tegen alle stereotypes van een
   * concept/relatie (M5-§6). Voor elke stereotype-id wordt de
   * effectieve attribute-set (incl. parent-stereotype via
   * `extends_stereotype`) gevalideerd via `validateAttributes`.
   * Errors van alle stereotypes worden samengevoegd; volgorde =
   * de volgorde waarin stereotypes geregistreerd zijn op het
   * element. Onbekende stereotype-ids worden stilletjes
   * geskipped — caller hoort eerst `canApplyStereotype` te
   * draaien voor stereotype-existence-validatie.
   *
   * TBD/NTB-sentinel-acceptance werkt automatisch via de
   * onderliggende `validateAttribute`: "TBD" en "NTB" zijn
   * geldige string-waarden voor required string- en string[]-
   * velden, dus de required-check passes. Voor enum-velden passes
   * de sentinel ook buiten `options` (zie `validateAttribute`).
   */
  validateStereotypeAttributes(
    stereotypeIds: readonly string[],
    values: Record<string, unknown>,
    options: ValidateOptions = {},
    /**
     * Top-level concept-fields voor virtuele attributen
     * (`storage_mode: "concept_description"`). Doorgegeven aan
     * `validateAttributes` zodat required-checks op `definition_text`
     * (Sharp) en `description_text` (C4) tegen de kernel-`description`
     * gebeuren — niet tegen `attributes[name]`.
     */
    conceptFields?: { description?: unknown },
  ): AttributeValidationError[] {
    const errors: AttributeValidationError[] = [];
    for (const stId of stereotypeIds) {
      const st = this.stereotypes.get(stId);
      if (!st) continue;
      const attrs = this.resolveStereotypeAttributes(st);
      if (attrs.length === 0) continue;
      errors.push(...validateAttributes(attrs, values, options, conceptFields));
    }
    return errors;
  }

  // === einde Stereotypes ===

  listActiveViewKinds(): ViewKind[] {
    return [...this.viewKinds.values()].filter((v) => v.fase1_active);
  }

  /**
   * Filter element-types that should appear in a given view-kind, applying
   * the view's `visible_layers` / `included_types` / `excluded_types` rules.
   */
  elementTypesForView(viewKindId: string): ElementType[] {
    const view = this.viewKinds.get(viewKindId);
    if (!view) {
      return [];
    }
    const all = this.listActiveElementTypes();

    if (view.included_types && view.included_types.length > 0) {
      return all.filter((t) => view.included_types?.includes(t.id) ?? false);
    }

    let filtered = all;
    if (view.visible_layers && view.visible_layers.length > 0) {
      filtered = filtered.filter((t) => view.visible_layers?.includes(t.layer) ?? false);
    }
    if (view.visible_aspects && view.visible_aspects.length > 0) {
      filtered = filtered.filter((t) => view.visible_aspects?.includes(t.aspect) ?? false);
    }
    if (view.excluded_types && view.excluded_types.length > 0) {
      filtered = filtered.filter((t) => !(view.excluded_types?.includes(t.id) ?? false));
    }
    return filtered;
  }
}

function mergeExtension(parent: ElementType, ext: RegistryExtension): ElementType {
  const mergedAttributes = [...(parent.attributes ?? []), ...(ext.added_attributes ?? [])];
  return {
    ...parent,
    id: ext.id,
    label: ext.label,
    ...(ext.description !== undefined ? { description: ext.description } : {}),
    attributes: mergedAttributes,
  };
}
